import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
} from '@mui/material';

interface DetailedCertificationStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  onClose: () => void;
  existingData?: any;
  driver?: any;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

export const DetailedCertificationStep: React.FC<DetailedCertificationStepProps> = ({
  onNext,
  onBack: _onBack,
  onClose,
  existingData,
  driver: _driver,
  isFirstStep: _isFirstStep,
  isLastStep: _isLastStep,
}) => {
  const [formData, setFormData] = useState({
    certificationTypeId: '',
    customTitle: '',
    issuedDate: '',
    expiryDate: '',
    issuingOrganization: '',
  });

  useEffect(() => {
    if (existingData) {
      setFormData(prev => ({ ...prev, ...existingData }));
    }
  }, [existingData]);

  // Mock certification types - in real app, this would come from API
  const certificationTypes = [
    { value: '1', label: 'CPR Certification' },
    { value: '2', label: 'Hazmat Certification' },
    { value: '3', label: 'First Aid Certification' },
    { value: '4', label: 'Safety Training Certificate' },
    { value: '5', label: 'Other' },
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Prepare data for saving
    const saveData = {
      certificationTypeId: parseInt(formData.certificationTypeId, 10) || 1,
      customTitle: formData.certificationTypeId === '5' ? formData.customTitle : null,
      issuedDate: formData.issuedDate,
      expiryDate: formData.expiryDate || null,
      issuingOrganization: formData.issuingOrganization,
    };

    onNext(saveData);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 'normal', mb: 4, textAlign: 'center' }}>
        Add Certification
      </Typography>

      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', textAlign: 'center' }}>
        This is where you add any certifications you hold, like CPR or Hazmat.
      </Typography>

      <Grid container spacing={3}>
        {/* Certification Type */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            * Certification title
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Select Certification Type</InputLabel>
            <Select
              value={formData.certificationTypeId}
              label="Select Certification Type"
              onChange={e => handleInputChange('certificationTypeId', e.target.value)}
              sx={{ backgroundColor: 'white' }}
            >
              {certificationTypes.map(cert => (
                <MenuItem key={cert.value} value={cert.value}>
                  {cert.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Custom certification title */}
          {formData.certificationTypeId === '5' && (
            <TextField
              fullWidth
              label="Custom Certification Title"
              value={formData.customTitle}
              onChange={e => handleInputChange('customTitle', e.target.value)}
              sx={{ mt: 2, backgroundColor: 'white' }}
            />
          )}
        </Grid>

        {/* Issuing Organization */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Issuing Organization
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter issuing organization"
            value={formData.issuingOrganization}
            onChange={e => handleInputChange('issuingOrganization', e.target.value)}
            sx={{ backgroundColor: 'white' }}
          />
        </Grid>

        {/* Issued Date */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            * Date Issued
          </Typography>
          <TextField
            fullWidth
            type="date"
            value={formData.issuedDate}
            onChange={e => handleInputChange('issuedDate', e.target.value)}
            sx={{ backgroundColor: 'white' }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        {/* Expiry Date (Optional) */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Expiry Date (Optional)
          </Typography>
          <TextField
            fullWidth
            type="date"
            value={formData.expiryDate}
            onChange={e => handleInputChange('expiryDate', e.target.value)}
            sx={{ backgroundColor: 'white' }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 6,
        }}
      >
        <Button
          variant="outlined"
          size="large"
          onClick={onClose}
          sx={{
            px: 4,
            py: 1.5,
            borderColor: '#1976d2',
            color: '#1976d2',
          }}
        >
          Close
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={!formData.certificationTypeId || !formData.issuedDate}
          sx={{
            backgroundColor: '#1976d2',
            '&:hover': { backgroundColor: '#1565c0' },
            px: 4,
            py: 1.5,
          }}
        >
          {isLastStep ? 'Complete' : 'Save & Continue'}
        </Button>
      </Box>
    </Box>
  );
};
